/* eslint-disable */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  FormControl as MuiFormControl,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DashboardLayout } from '../components/DashboardLayout';
import { PageIndices, TabIndices } from '../common/i-doc/i-doc.registry.ts';
import { PageHint } from '../components/common/PageHint';
import { calculateMetric } from '../services/calculated-metrics.service';
import { MetricInfoModal } from '../components/CalculatedMetrics/MetricInfoModal';
import { AdaptiveTabs, AdaptiveTabItem } from '../components/common/AdaptiveTabs';

const CALC_TABS: AdaptiveTabItem[] = [
  { value: 'bmi',  label: 'BMI' },
  { value: 'bmr',  label: 'BMR' },
  { value: 'wdn',  label: 'WDN' },
  { value: 'amr',  label: 'AMR' },
  { value: 'whr',  label: 'WHR' },
  { value: 'amdr', label: 'AMDR' },
  { value: 'wdna', label: 'WDNA' },
];

const activityLevels = [
  { key: 'sedentary', label: 'Сидячий спосіб життя' },
  { key: 'lightly_active', label: 'Легка активність (1-3 тренування на тиждень)' },
  { key: 'moderately_active', label: 'Помірна активність (3-5 тренувань на тиждень)' },
  { key: 'very_active', label: 'Висока активність (6-7 тренувань на тиждень)' },
  { key: 'extra_active', label: 'Дуже висока активність (фізична робота, тренування 2 р/день)' },
];

const dietSchemes = [
  { key: 'balanced', label: 'Збалансована' },
  { key: 'protein_heavy', label: 'Білкова' },
  { key: 'mixed', label: 'Змішана' },
  { key: 'carb_heavy', label: 'Вуглеводна' },
];

export const CalculatedMetricsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') ?? 'bmi';
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [selectedMetricForInfo, setSelectedMetricForInfo] = useState<string | null>(null);

  const handleOpenInfoModal = (metric: string) => {
    setSelectedMetricForInfo(metric);
    setIsInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setIsInfoModalOpen(false);
    setSelectedMetricForInfo(null);
  };

  // BMI State
  const [bmiWeight, setBmiWeight] = useState('');
  const [bmiHeight, setBmiHeight] = useState('');
  const [bmiResult, setBmiResult] = useState<any>(null);
  const [bmiLoading, setBmiLoading] = useState(false);
  const [bmiError, setBmiError] = useState<string | null>(null);

  // BMR State
  const [bmrWeight, setBmrWeight] = useState('');
  const [bmrHeight, setBmrHeight] = useState('');
  const [bmrAge, setBmrAge] = useState('');
  const [bmrGender, setBmrGender] = useState('female');
  const [bmrResult, setBmrResult] = useState<any>(null);
  const [bmrLoading, setBmrLoading] = useState(false);
  const [bmrError, setBmrError] = useState<string | null>(null);

  // Water Norm State (WDN)
  const [waterWeight, setWaterWeight] = useState('');
  const [waterResult, setWaterResult] = useState<any>(null);
  const [waterLoading, setWaterLoading] = useState(false);
  const [waterError, setWaterError] = useState<string | null>(null);

  // AMR State
  const [amrWeight, setAmrWeight] = useState('');
  const [amrHeight, setAmrHeight] = useState('');
  const [amrAge, setAmrAge] = useState('');
  const [amrGender, setAmrGender] = useState('female');
  const [amrActivityLevel, setAmrActivityLevel] = useState('sedentary');
  const [amrResult, setAmrResult] = useState<any>(null);
  const [amrLoading, setAmrLoading] = useState(false);
  const [amrError, setAmrError] = useState<string | null>(null);

  // WHR State
  const [whrWaist, setWhrWaist] = useState('');
  const [whrHips, setWhrHips] = useState('');
  const [whrGender, setWhrGender] = useState('female');
  const [whrResult, setWhrResult] = useState<any>(null);
  const [whrLoading, setWhrLoading] = useState(false);
  const [whrError, setWhrError] = useState<string | null>(null);

  // AMDR State (formerly BJU)
  const [bjuAmr, setBjuAmr] = useState('');
  const [bjuScheme, setBjuScheme] = useState('balanced');
  const [bjuResult, setBjuResult] = useState<any>(null);
  const [bjuLoading, setBjuLoading] = useState(false);
  const [bjuError, setBjuError] = useState<string | null>(null);

  // WDNA State
  const [wdnaWeight, setWdnaWeight] = useState('');
  const [wdnaAge, setWdnaAge] = useState('');
  const [wdnaGender, setWdnaGender] = useState('female');
  const [wdnaActivityLevel, setWdnaActivityLevel] = useState('sedentary');
  const [wdnaResult, setWdnaResult] = useState<any>(null);
  const [wdnaLoading, setWdnaLoading] = useState(false);
  const [wdnaError, setWdnaError] = useState<string | null>(null);

  const handleBmiCalculate = async () => {
    setBmiLoading(true);
    setBmiError(null);
    setBmiResult(null);
    try {
      const result = await calculateMetric('bmi', { weight: Number(bmiWeight), height: Number(bmiHeight) });
      setBmiResult(result);
    } catch (error: any) {
      setBmiError(error.response?.data?.message || 'Не вдалося розрахувати BMI.');
    } finally {
      setBmiLoading(false);
    }
  };

  const handleBmrCalculate = async () => {
    setBmrLoading(true);
    setBmrError(null);
    setBmrResult(null);
    try {
      const result = await calculateMetric('bmr', { weight: Number(bmrWeight), height: Number(bmrHeight), age: Number(bmrAge), gender: bmrGender });
      setBmrResult(result);
    } catch (error: any) {
      setBmrError(error.response?.data?.message || 'Не вдалося розрахувати BMR.');
    } finally {
      setBmrLoading(false);
    }
  };

  const handleWaterCalculate = async () => {
    setWaterLoading(true);
    setWaterError(null);
    setWaterResult(null);
    try {
      const result = await calculateMetric('wdn', { weight: Number(waterWeight) });
      setWaterResult(result);
    } catch (error: any) {
      setWaterError(error.response?.data?.message || 'Не вдалося розрахувати добову норму води.');
    } finally {
      setWaterLoading(false);
    }
  };

  const handleAmrCalculate = async () => {
    setAmrLoading(true);
    setAmrError(null);
    setAmrResult(null);
    try {
      const result = await calculateMetric('amr', {
        weight: Number(amrWeight),
        height: Number(amrHeight),
        age: Number(amrAge),
        gender: amrGender,
        activityLevel: amrActivityLevel,
      });
      setAmrResult(result);
    } catch (error: any) {
      setAmrError(error.response?.data?.message || 'Не вдалося розрахувати AMR.');
    } finally {
      setAmrLoading(false);
    }
  };

  const handleWhrCalculate = async () => {
    setWhrLoading(true);
    setWhrError(null);
    setWhrResult(null);
    try {
      const result = await calculateMetric('whr', { waist: Number(whrWaist), hips: Number(whrHips), gender: whrGender });
      setWhrResult(result);
    } catch (error: any) {
      setWhrError(error.response?.data?.message || 'Не вдалося розрахувати WHR.');
    } finally {
      setWhrLoading(false);
    }
  };

  const handleBjuCalculate = async () => {
    setBjuLoading(true);
    setBjuError(null);
    setBjuResult(null);
    try {
      const result = await calculateMetric('amdr', { amr: Number(bjuAmr), scheme: bjuScheme });
      setBjuResult(result);
    } catch (error: any) {
      setBjuError(error.response?.data?.message || 'Не вдалося розрахувати AMDR.');
    } finally {
      setBjuLoading(false);
    }
  };

  const handleWdnaCalculate = async () => {
    setWdnaLoading(true);
    setWdnaError(null);
    setWdnaResult(null);
    try {
      const result = await calculateMetric('wdna', {
        weight: Number(wdnaWeight),
        gender: wdnaGender,
        age: Number(wdnaAge),
        activityLevel: wdnaActivityLevel
      });
      setWdnaResult(result);
    } catch (error: any) {
      setWdnaError(error.response?.data?.message || 'Не вдалося розрахувати добову норму води для спортсменів.');
    } finally {
      setWdnaLoading(false);
    }
  };

  return (
    <DashboardLayout data-i-doc={PageIndices.CALCULATED_METRICS}>
      <PageHint iDoc={PageIndices.CALCULATED_METRICS} />
      <AdaptiveTabs tabs={CALC_TABS} searchParamKey="tab" defaultValue="bmi" />

      {currentTab === 'bmi' && (
        <Box sx={{ pt: 3 }} data-i-doc={TabIndices.CALCULATED_METRICS_BMI}>
          <PageHint iDoc={TabIndices.CALCULATED_METRICS_BMI} />
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                Розрахунок Індексу маси тіла (BMI)
              </Typography>
              <IconButton size="small" onClick={() => handleOpenInfoModal('bmi')}><InfoOutlinedIcon fontSize="small" /></IconButton>
            </Box>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField label="Вага (кг)" type="number" fullWidth value={bmiWeight} onChange={(e) => setBmiWeight(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Зріст (м)" type="number" fullWidth value={bmiHeight} onChange={(e) => setBmiHeight(e.target.value)} />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleBmiCalculate} disabled={bmiLoading}>
                {bmiLoading ? <CircularProgress size={24} /> : 'Розрахувати'}
              </Button>
            </Box>
            {bmiError && <Alert severity="error" sx={{ mt: 2 }}>{bmiError}</Alert>}
            {bmiResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Результат: {bmiResult.result}</Typography>
                <Typography>Категорія: {bmiResult.resultDetails?.category}</Typography>
                <Typography>Коментар: {bmiResult.resultDetails?.resultComment}</Typography>
                <Typography sx={{ mt: 2 }}>Пояснення: {bmiResult.explanation}</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {currentTab === 'bmr' && (
        <Box sx={{ pt: 3 }} data-i-doc={TabIndices.CALCULATED_METRICS_BMR}>
          <PageHint iDoc={TabIndices.CALCULATED_METRICS_BMR} />
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>Розрахунок Базального метаболізму (BMR)</Typography>
              <IconButton size="small" onClick={() => handleOpenInfoModal('bmr')}><InfoOutlinedIcon fontSize="small" /></IconButton>
            </Box>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}><TextField label="Вага (кг)" type="number" fullWidth value={bmrWeight} onChange={(e) => setBmrWeight(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Зріст (см)" type="number" fullWidth value={bmrHeight} onChange={(e) => setBmrHeight(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Вік (років)" type="number" fullWidth value={bmrAge} onChange={(e) => setBmrAge(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>
                <MuiFormControl component="fieldset">
                  <FormLabel component="legend">Стать</FormLabel>
                  <RadioGroup row value={bmrGender} onChange={(e) => setBmrGender(e.target.value)}>
                    <FormControlLabel value="female" control={<Radio />} label="Жінка" />
                    <FormControlLabel value="male" control={<Radio />} label="Чоловік" />
                  </RadioGroup>
                </MuiFormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleBmrCalculate} disabled={bmrLoading}>
                {bmrLoading ? <CircularProgress size={24} /> : 'Розрахувати'}
              </Button>
            </Box>
            {bmrError && <Alert severity="error" sx={{ mt: 2 }}>{bmrError}</Alert>}
            {bmrResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Результат: {bmrResult.result} ккал/день</Typography>
                <Typography>{bmrResult.resultDetails?.resultComment}</Typography>
                <Typography sx={{ mt: 2 }}>Пояснення: {bmrResult.explanation}</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {currentTab === 'wdn' && (
        <Box sx={{ pt: 3 }} data-i-doc={TabIndices.CALCULATED_METRICS_WDN}>
          <PageHint iDoc={TabIndices.CALCULATED_METRICS_WDN} />
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>Розрахунок Добової норми води (WDN)</Typography>
              <IconButton size="small" onClick={() => handleOpenInfoModal('wdn')}><InfoOutlinedIcon fontSize="small" /></IconButton>
            </Box>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField label="Вага (кг)" type="number" fullWidth value={waterWeight} onChange={(e) => setWaterWeight(e.target.value)} />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleWaterCalculate} disabled={waterLoading}>{waterLoading ? <CircularProgress size={24} /> : 'Розрахувати'}</Button>
            </Box>
            {waterError && <Alert severity="error" sx={{ mt: 2 }}>{waterError}</Alert>}
            {waterResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Результат: {waterResult.result} мл</Typography>
                <Typography>{waterResult.resultDetails?.resultComment}</Typography>
                <Typography sx={{ mt: 2 }}>Пояснення: {waterResult.explanation}</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {currentTab === 'amr' && (
        <Box sx={{ pt: 3 }} data-i-doc={TabIndices.CALCULATED_METRICS_AMR}>
          <PageHint iDoc={TabIndices.CALCULATED_METRICS_AMR} />
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>Розрахунок Активного рівня метаболізму (AMR)</Typography>
              <IconButton size="small" onClick={() => handleOpenInfoModal('amr')}><InfoOutlinedIcon fontSize="small" /></IconButton>
            </Box>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}><TextField label="Вага (кг)" type="number" fullWidth value={amrWeight} onChange={(e) => setAmrWeight(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Зріст (см)" type="number" fullWidth value={amrHeight} onChange={(e) => setAmrHeight(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Вік (років)" type="number" fullWidth value={amrAge} onChange={(e) => setAmrAge(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>
                <MuiFormControl component="fieldset">
                  <FormLabel component="legend">Стать</FormLabel>
                  <RadioGroup row value={amrGender} onChange={(e) => setAmrGender(e.target.value)}>
                    <FormControlLabel value="female" control={<Radio />} label="Жінка" />
                    <FormControlLabel value="male" control={<Radio />} label="Чоловік" />
                  </RadioGroup>
                </MuiFormControl>
              </Grid>
              <Grid item xs={12}>
                <MuiFormControl fullWidth>
                  <InputLabel>Рівень активності</InputLabel>
                  <Select value={amrActivityLevel} label="Рівень активності" onChange={(e) => setAmrActivityLevel(e.target.value as string)}>
                    {activityLevels.map((l) => <MenuItem key={l.key} value={l.key}>{l.label}</MenuItem>)}
                  </Select>
                </MuiFormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleAmrCalculate} disabled={amrLoading}>{amrLoading ? <CircularProgress size={24} /> : 'Розрахувати'}</Button>
            </Box>
            {amrError && <Alert severity="error" sx={{ mt: 2 }}>{amrError}</Alert>}
            {amrResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Результат: {amrResult.result} ккал/день</Typography>
                <Typography>{amrResult.resultDetails?.resultComment}</Typography>
                <Typography sx={{ mt: 2 }}>Пояснення: {amrResult.explanation}</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {currentTab === 'whr' && (
        <Box sx={{ pt: 3 }} data-i-doc={TabIndices.CALCULATED_METRICS_WHR}>
          <PageHint iDoc={TabIndices.CALCULATED_METRICS_WHR} />
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>Розрахунок WHR</Typography>
              <IconButton size="small" onClick={() => handleOpenInfoModal('whr')}><InfoOutlinedIcon fontSize="small" /></IconButton>
            </Box>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}><TextField label="Обхват талії (см)" type="number" fullWidth value={whrWaist} onChange={(e) => setWhrWaist(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Обхват стегон (см)" type="number" fullWidth value={whrHips} onChange={(e) => setWhrHips(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>
                <MuiFormControl component="fieldset">
                  <FormLabel component="legend">Стать</FormLabel>
                  <RadioGroup row value={whrGender} onChange={(e) => setWhrGender(e.target.value)}>
                    <FormControlLabel value="female" control={<Radio />} label="Жінка" />
                    <FormControlLabel value="male" control={<Radio />} label="Чоловік" />
                  </RadioGroup>
                </MuiFormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleWhrCalculate} disabled={whrLoading}>{whrLoading ? <CircularProgress size={24} /> : 'Розрахувати'}</Button>
            </Box>
            {whrError && <Alert severity="error" sx={{ mt: 2 }}>{whrError}</Alert>}
            {whrResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Результат: {whrResult.result}</Typography>
                <Typography>{whrResult.resultDetails?.resultComment}</Typography>
                <Typography sx={{ mt: 2 }}>Пояснення: {whrResult.explanation}</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {currentTab === 'amdr' && (
        <Box sx={{ pt: 3 }} data-i-doc={TabIndices.CALCULATED_METRICS_AMDR}>
          <PageHint iDoc={TabIndices.CALCULATED_METRICS_AMDR} />
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>Розрахунок AMDR</Typography>
              <IconButton size="small" onClick={() => handleOpenInfoModal('amdr')}><InfoOutlinedIcon fontSize="small" /></IconButton>
            </Box>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}><TextField label="AMR" type="number" fullWidth value={bjuAmr} onChange={(e) => setBjuAmr(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>
                <MuiFormControl fullWidth>
                  <InputLabel>Схема харчування</InputLabel>
                  <Select value={bjuScheme} label="Схема харчування" onChange={(e) => setBjuScheme(e.target.value as string)}>
                    {dietSchemes.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                  </Select>
                </MuiFormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleBjuCalculate} disabled={bjuLoading}>{bjuLoading ? <CircularProgress size={24} /> : 'Розрахувати'}</Button>
            </Box>
            {bjuError && <Alert severity="error" sx={{ mt: 2 }}>{bjuError}</Alert>}
            {bjuResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Результат</Typography>
                <Typography>{bjuResult.resultDetails?.resultComment}</Typography>
                <Typography sx={{ mt: 2 }}>Пояснення: {bjuResult.explanation}</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {currentTab === 'wdna' && (
        <Box sx={{ pt: 3 }} data-i-doc={TabIndices.CALCULATED_METRICS_WDNA}>
          <PageHint iDoc={TabIndices.CALCULATED_METRICS_WDNA} />
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>Розрахунок WDNA</Typography>
              <IconButton size="small" onClick={() => handleOpenInfoModal('wdna')}><InfoOutlinedIcon fontSize="small" /></IconButton>
            </Box>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}><TextField label="Вага (кг)" type="number" fullWidth value={wdnaWeight} onChange={(e) => setWdnaWeight(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Вік (років)" type="number" fullWidth value={wdnaAge} onChange={(e) => setWdnaAge(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>
                <MuiFormControl component="fieldset">
                  <FormLabel component="legend">Стать</FormLabel>
                  <RadioGroup row value={wdnaGender} onChange={(e) => setWdnaGender(e.target.value)}>
                    <FormControlLabel value="female" control={<Radio />} label="Жінка" />
                    <FormControlLabel value="male" control={<Radio />} label="Чоловік" />
                  </RadioGroup>
                </MuiFormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MuiFormControl fullWidth>
                  <InputLabel>Рівень активності</InputLabel>
                  <Select value={wdnaActivityLevel} label="Рівень активності" onChange={(e) => setWdnaActivityLevel(e.target.value as string)}>
                    {activityLevels.map((l) => <MenuItem key={l.key} value={l.key}>{l.label}</MenuItem>)}
                  </Select>
                </MuiFormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleWdnaCalculate} disabled={wdnaLoading}>{wdnaLoading ? <CircularProgress size={24} /> : 'Розрахувати'}</Button>
            </Box>
            {wdnaError && <Alert severity="error" sx={{ mt: 2 }}>{wdnaError}</Alert>}
            {wdnaResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Результат: {wdnaResult.result} мл</Typography>
                <Typography>{wdnaResult.resultDetails?.resultComment}</Typography>
                <Typography sx={{ mt: 2 }}>Пояснення: {wdnaResult.explanation}</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      <MetricInfoModal
        open={isInfoModalOpen}
        onClose={handleCloseInfoModal}
        metricName={selectedMetricForInfo}
      />
    </DashboardLayout>
  );
};

export default CalculatedMetricsPage;
